import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';

/* ─── BACKGROUND ORBS ───────────────────────────────────── */
const BackgroundOrbs = () => {
  const c1 = useRef(null), c2 = useRef(null), c3 = useRef(null), c4 = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const items = [
      { ref: c1, speed: 0.035 }, { ref: c2, speed: -0.025 },
      { ref: c3, speed: 0.055 }, { ref: c4, speed: -0.04 },
    ];
    const onScroll = () => {
      const y = window.scrollY;
      items.forEach(({ ref, speed }) => {
        if (ref.current) ref.current.style.transform = `translateY(${y * speed}px)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className="cz-orbs-wrap" aria-hidden="true">
      <div ref={c1} className="cz-orc"><div className="cz-orb cz-orb-1"/></div>
      <div ref={c2} className="cz-orc"><div className="cz-orb cz-orb-2"/></div>
      <div ref={c3} className="cz-orc"><div className="cz-orb cz-orb-3"/></div>
      <div ref={c4} className="cz-orc"><div className="cz-orb cz-orb-4"/></div>
    </div>
  );
};

/* ─── ICONS ─────────────────────────────────────────────── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconCheck = () => (
  <svg width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
);

/* ─── COTIZAR PAGE ───────────────────────────────────────── */
const CotizarPage = () => {
  const navigate = useNavigate();
  const [quotationData, setQuotationData]   = useState({ company:'', contact_person:'', email:'', phone:'', estimated_cards:'', comments:'' });
  const [quotationSent, setQuotationSent]   = useState(false);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [submitError, setSubmitError]       = useState('');
  const [priceRanges, setPriceRanges]       = useState([]);
  const [quantityType, setQuantityType]     = useState('preset');
  const [customQuantity, setCustomQuantity] = useState(1);
  const [quantityError, setQuantityError]   = useState('');

  useEffect(() => {
    axios.get(`${API}/public/quotations/config`).then(res => {
      if (res.data?.price_table) setPriceRanges(res.data.price_table);
    }).catch(() => {});
  }, []);

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (quantityType === 'custom' && (!parseInt(customQuantity) || parseInt(customQuantity) < 1)) {
      setQuantityError('Ingresa una cantidad válida.'); return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/quotations`, quotationData);
      setQuotationSent(true);
      setQuotationData({ company:'', contact_person:'', email:'', phone:'', estimated_cards:'', comments:'' });
    } catch (err) {
      // Previously only logged to console — the button just re-enabled
      // itself with zero explanation, leaving the visitor unsure whether
      // the request went through or silently failed. Form data itself was
      // never actually lost (this clear only ran on success, above), but
      // there was no way to tell that without opening devtools.
      console.error(err);
      setSubmitError(err?.response?.data?.detail || 'No pudimos enviar tu solicitud. Intenta de nuevo.');
    }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="cz-root">
      <BackgroundOrbs/>
      <header className="cz-nav">
        <div className="cz-nav-inner">
          <ContactLogo clickable />
          <button onClick={() => navigate('/')} className="cz-btn-back">
            <IconArrowLeft/> Volver
          </button>
        </div>
      </header>

      <main className="cz-main">
        <div className="cz-panel">
          {quotationSent ? (
            <div className="cz-success">
              <div className="cz-success-ico"><IconCheck/></div>
              <h3>Solicitud recibida</h3>
              <p>Te contactaremos dentro de las próximas 24 horas hábiles con una propuesta personalizada.</p>
            </div>
          ) : (
            <>
              <div className="cz-head">
                <h1>Solicitar cotización</h1>
                <p>Te responderemos en menos de 24 horas hábiles.</p>
              </div>
              <form onSubmit={handleQuotationSubmit}>
                <div className="cz-field">
                  <label>Empresa <span className="cz-req">*</span></label>
                  <input type="text" required value={quotationData.company} onChange={e => setQuotationData({...quotationData, company: e.target.value})} placeholder="ACME S.A." />
                </div>
                <div className="cz-row">
                  <div className="cz-field">
                    <label>Persona de contacto <span className="cz-req">*</span></label>
                    <input type="text" required value={quotationData.contact_person} onChange={e => setQuotationData({...quotationData, contact_person: e.target.value})} placeholder="Ana Rodríguez" />
                  </div>
                  <div className="cz-field">
                    <label>Correo electrónico <span className="cz-req">*</span></label>
                    <input type="email" required value={quotationData.email} onChange={e => setQuotationData({...quotationData, email: e.target.value})} placeholder="ana@empresa.com" />
                  </div>
                </div>
                <div className="cz-row">
                  <div className="cz-field">
                    <label>Teléfono <span className="cz-req">*</span></label>
                    <input type="tel" required value={quotationData.phone} onChange={e => setQuotationData({...quotationData, phone: e.target.value})} placeholder="+56 9 1234 5678" />
                  </div>
                  <div className="cz-field">
                    <label>Cantidad estimada <span className="cz-req">*</span></label>
                    {quantityType === 'preset' ? (
                      <select required value={quotationData.estimated_cards} onChange={e => setQuotationData({...quotationData, estimated_cards: e.target.value})}>
                        <option value="">Selecciona un rango</option>
                        {priceRanges.map((r, i) => <option key={i} value={r.range}>{r.description}</option>)}
                      </select>
                    ) : (
                      <div className="cz-qty-row">
                        <button type="button" className="cz-qty-btn" onClick={() => { const v = Math.max(1, customQuantity-1); setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); }}>−</button>
                        <input type="number" min="1" value={customQuantity} className="cz-qty-input" onChange={e => { const v = parseInt(e.target.value)||1; setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); setQuantityError(v<1?'Mínimo 1':''); }} />
                        <button type="button" className="cz-qty-btn" onClick={() => { const v = customQuantity+1; setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); }}>+</button>
                      </div>
                    )}
                    <div className="cz-qty-toggle">
                      <label><input type="radio" value="preset" checked={quantityType==='preset'} onChange={() => { setQuantityType('preset'); setQuantityError(''); setQuotationData({...quotationData, estimated_cards: ''}); }} /> Rangos</label>
                      <label><input type="radio" value="custom" checked={quantityType==='custom'} onChange={() => { setQuantityType('custom'); setQuotationData({...quotationData, estimated_cards: String(customQuantity)}); }} /> Exacto</label>
                    </div>
                    {quantityError && <p className="cz-error">{quantityError}</p>}
                  </div>
                </div>
                <div className="cz-field">
                  <label>Descripción del proyecto</label>
                  <textarea rows="3" value={quotationData.comments} onChange={e => setQuotationData({...quotationData, comments: e.target.value})} placeholder="Cuéntanos sobre tu empresa y necesidades..." />
                </div>
                {submitError && <p className="cz-submit-error">{submitError}</p>}
                <button type="submit" disabled={isSubmitting} className="cz-submit">{isSubmitting ? 'Enviando...' : 'Solicitar cotización'}</button>
              </form>
            </>
          )}
        </div>
      </main>

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
:root{
  --cz-bg:#F9F9F8;
  --cz-text:#111111;
  --cz-muted:#888888;
  --cz-border:#E5E5E3;
  --cz-dark:#0F172A;
  --cz-blue:#2563EB;
}

.cz-root{font-family:'Inter',system-ui,sans-serif;background:var(--cz-bg);color:var(--cz-text);min-height:100vh;line-height:1.6;overflow-x:hidden;position:relative}

/* ── ORBS ── */
.cz-orbs-wrap{position:fixed;inset:0;pointer-events:none;z-index:0;overflow:hidden}
.cz-orc{position:absolute;will-change:transform}
.cz-orb{position:absolute;border-radius:50%;top:0;left:0}
.cz-orc:nth-child(1){width:800px;height:800px;top:-200px;left:-280px}
.cz-orb-1{width:100%;height:100%;background:radial-gradient(circle at 50%,rgba(59,130,246,.16) 0%,rgba(59,130,246,.07) 40%,transparent 66%);animation:czOrb1 22s ease-in-out infinite}
.cz-orc:nth-child(2){width:660px;height:660px;top:8%;right:-220px}
.cz-orb-2{width:100%;height:100%;background:radial-gradient(circle at 50%,rgba(99,102,241,.12) 0%,rgba(99,102,241,.05) 40%,transparent 66%);animation:czOrb2 26s ease-in-out infinite}
.cz-orc:nth-child(3){width:580px;height:580px;top:52%;left:-160px}
.cz-orb-3{width:100%;height:100%;background:radial-gradient(circle at 50%,rgba(34,197,94,.10) 0%,rgba(34,197,94,.04) 40%,transparent 66%);animation:czOrb3 24s ease-in-out infinite}
.cz-orc:nth-child(4){width:520px;height:520px;bottom:-160px;right:-120px}
.cz-orb-4{width:100%;height:100%;background:radial-gradient(circle at 50%,rgba(168,85,247,.10) 0%,rgba(168,85,247,.04) 40%,transparent 66%);animation:czOrb4 28s ease-in-out infinite}
@keyframes czOrb1{0%,100%{transform:translate(0,0)}50%{transform:translate(40px,30px)}}
@keyframes czOrb2{0%,100%{transform:translate(0,0)}50%{transform:translate(-35px,25px)}}
@keyframes czOrb3{0%,100%{transform:translate(0,0)}50%{transform:translate(30px,-25px)}}
@keyframes czOrb4{0%,100%{transform:translate(0,0)}50%{transform:translate(-30px,-30px)}}

.cz-nav{position:sticky;top:0;z-index:10;background:rgba(249,249,248,.8);backdrop-filter:blur(12px);border-bottom:1px solid var(--cz-border)}
.cz-nav-inner{max-width:1200px;margin:0 auto;height:64px;display:flex;align-items:center;justify-content:space-between;padding:0 32px}
.cz-btn-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid var(--cz-border);border-radius:100px;padding:8px 16px;font-size:13px;font-weight:600;color:var(--cz-text);cursor:pointer;font-family:inherit;transition:background .15s}
.cz-btn-back:hover{background:#fff}

.cz-main{position:relative;z-index:1;display:flex;justify-content:center;padding:64px 24px 96px}
.cz-panel{width:100%;max-width:560px;background:#fff;border:1px solid var(--cz-border);border-radius:20px;padding:40px;box-shadow:0 20px 60px rgba(0,0,0,.06)}

.cz-head h1{font-size:26px;font-weight:300;letter-spacing:-.01em;margin-bottom:6px}
.cz-head p{font-size:14px;color:var(--cz-muted);margin-bottom:28px}

.cz-field{margin-bottom:18px}
.cz-field label{display:block;font-size:13px;font-weight:600;color:var(--cz-text);margin-bottom:6px}
.cz-req{color:#DC2626}
.cz-field input,.cz-field select,.cz-field textarea{width:100%;border:1px solid var(--cz-border);border-radius:10px;padding:11px 14px;font-size:14px;font-family:inherit;color:var(--cz-text);background:#fff;transition:border-color .15s}
.cz-field input:focus,.cz-field select:focus,.cz-field textarea:focus{outline:none;border-color:var(--cz-blue)}
.cz-field textarea{resize:vertical}

.cz-row{display:grid;grid-template-columns:1fr 1fr;gap:16px}

.cz-qty-row{display:flex;align-items:center;gap:10px}
.cz-qty-btn{width:36px;height:38px;border-radius:8px;border:1px solid var(--cz-border);background:#fff;font-size:16px;cursor:pointer;flex-shrink:0}
.cz-qty-input{text-align:center}
.cz-qty-toggle{display:flex;gap:16px;margin-top:8px}
.cz-qty-toggle label{display:flex;align-items:center;gap:5px;font-size:12px;color:var(--cz-muted);cursor:pointer;font-weight:400}
.cz-qty-toggle input{accent-color:var(--cz-dark)}
.cz-error{font-size:12px;color:#DC2626;margin-top:4px}

.cz-submit-error{color:#DC2626;font-size:13px;margin:0 0 12px;text-align:center}
.cz-submit{width:100%;height:48px;border-radius:100px;background:var(--cz-dark);color:#fff;font-size:14px;font-weight:700;border:none;cursor:pointer;font-family:inherit;margin-top:8px;transition:opacity .15s}
.cz-submit:hover:not(:disabled){opacity:.85}
.cz-submit:disabled{opacity:.45;cursor:not-allowed}

.cz-success{text-align:center;padding:24px 0}
.cz-success-ico{width:52px;height:52px;border-radius:50%;background:#f0fdf4;border:1px solid #bbf7d0;color:#22C55E;display:flex;align-items:center;justify-content:center;margin:0 auto 16px}
.cz-success h3{font-size:18px;font-weight:700;margin-bottom:8px}
.cz-success p{font-size:14px;color:var(--cz-muted);line-height:1.6}

@media(max-width:640px){
  .cz-nav-inner{padding:0 20px}
  .cz-main{padding:40px 16px 64px}
  .cz-panel{padding:28px 22px}
  .cz-row{grid-template-columns:1fr}
}
@media(prefers-reduced-motion:reduce){
  .cz-orc,.cz-orb{will-change:auto;animation:none}
}
`;

export default CotizarPage;
